import { zodResolver } from '@hookform/resolvers/zod'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'
import type { emailValidationType } from '@/utils/schemas'
import { emailValidationSchema } from '@/utils/schemas'
import useSmallMobileMediaQuery from '@/hooks/useSmallMobileMediaQuery'

interface OTPVerificationFormProps {
  email: string
  onVerify: (otp: string) => void
  onResend: () => void
  isLoadingVerify: boolean
  isLoadingResend: boolean
  resendCooldownSeconds?: number
}

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export const OTPVerificationForm: React.FC<OTPVerificationFormProps> = ({
  email,
  onVerify,
  onResend,
  isLoadingVerify,
  isLoadingResend,
  resendCooldownSeconds,
}) => {
  const { t } = useTranslation()
  const [cooldown, setCooldown] = useState(0)

  useEffect(() => {
    if (cooldown <= 0) return
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [cooldown])

  const handleResend = (e: React.MouseEvent) => {
    e.preventDefault()
    onResend()
    if (resendCooldownSeconds) setCooldown(resendCooldownSeconds)
  }

  const form = useForm<emailValidationType>({
    resolver: zodResolver(emailValidationSchema),
    defaultValues: {
      pin: '',
    },
  })

  const { handleSubmit, control } = form

  const onSubmit = (data: { pin: string }) => {
    onVerify(data.pin)
  }

  const isSmallMobile = useSmallMobileMediaQuery()

  return (
    <div className="flex flex-col gap-8 w-full max-w-[500px] items-center bg-baseColorHighlight rounded-3xl p-8 border border-neutralColor5">
      <div className="flex flex-col gap-5 w-full items-center">
        <p className="text-[32px] font-medium text-center">
          {t('signUp.emailVerification.title')}
        </p>
        <p className="text-base font-medium text-neutralColor80 tracking-tight text-pretty text-center">
          {t('signUp.emailVerification.weSentA')} {email}{' '}
          {t('signUp.emailVerification.pleaseCheckYourEmail')}
        </p>
      </div>

      <Form {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-6 items-center w-full"
        >
          <FormField
            control={control}
            name="pin"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <InputOTP
                    maxLength={6}
                    {...field}
                    disabled={isLoadingResend || isLoadingVerify}
                  >
                    <InputOTPGroup
                      className={`flex gap-6 w-full  ${
                        isSmallMobile ? 'gap-1' : 'max-md:gap-2'
                      }`}
                    >
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </FormControl>
              </FormItem>
            )}
          />

          <p className="text-neutralColor80 text-sm font-normal tracking-tight">
            {t('signUp.emailVerification.typeThe6DigitCode')}
          </p>

          <Button
            disabled={!form.formState.isValid || isLoadingVerify}
            type="submit"
            className="py-3 px-5 font-medium tracking-widest h-[46px] w-full rounded-3xl"
            isLoading={isLoadingVerify}
          >
            {t('signUp.emailVerification.continue')}
          </Button>

          <Button
            variant="ghost"
            onClick={handleResend}
            disabled={isLoadingResend || cooldown > 0}
            className="w-min p-0 h-5"
          >
            <p
              className={`text-sm font-normal ${
                cooldown > 0 ? 'text-neutralColor60' : 'text-dynamicBlue'
              }`}
            >
              {cooldown > 0
                ? t('signUp.emailVerification.resendIn', {
                    time: formatTime(cooldown),
                  })
                : isLoadingResend
                  ? t('common.loading')
                  : t('signUp.emailVerification.clickToResend')}
            </p>
          </Button>
        </form>
      </Form>
    </div>
  )
}
